import React, { useState } from "react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Download, RefreshCw, XCircle, ExternalLink } from "lucide-react";

const STATUS_STYLE = {
  completed: "bg-emerald-100 text-emerald-800",
  requested: "bg-amber-100 text-amber-800",
  cancelled: "bg-zinc-200 text-zinc-700",
};

export const ESignStatusBadge = ({ status }) => (
  <span data-testid="esign-status-badge"
    className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase ${STATUS_STYLE[status] || "bg-red-100 text-red-800"}`}>
    {status}
  </span>
);

export default function ESignRequestStatus({ request, onChange, mockOtpHint }) {
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { const d = await fn(); if (okMsg) toast.success(okMsg); onChange?.(d); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sign = () => run(async () => (await api.post(`/esign/requests/${request.id}/mock-sign`, { otp })).data, "Document signed");
  const refresh = () => run(async () => (await api.get(`/esign/requests/${request.id}`)).data);
  const cancel = () => run(async () => { await api.post(`/esign/requests/${request.id}/cancel`); return { ...request, status: "cancelled" }; }, "Request cancelled");
  const download = () => downloadBlob(`/esign/requests/${request.id}/download`, request.file_name.replace(/\.pdf$/i, "") + "-signed.pdf");
  const pending = request.status === "requested";

  return (
    <div className="space-y-3 text-sm" data-testid="esign-request-status">
      <div className="flex items-center gap-2 flex-wrap">
        <ESignStatusBadge status={request.status} />
        <span className="text-muted-foreground text-xs">Signer: <b>{request.signer_name}</b> ({request.signer_identifier_masked})</span>
        <span className="text-muted-foreground text-xs font-mono">{request.provider_document_id}</span>
      </div>
      {pending && request.provider === "mock" && (
        <div className="flex gap-2 items-center">
          <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Aadhaar OTP" maxLength={6}
            className="w-36 rounded-sm font-mono" data-testid="esign-otp-input" />
          <Button size="sm" onClick={sign} disabled={busy || otp.length < 6} className="rounded-sm gap-1.5" data-testid="esign-verify-otp-btn">
            <CheckCircle2 className="w-3.5 h-3.5" /> Verify &amp; sign
          </Button>
          {mockOtpHint && <span className="text-[11px] text-muted-foreground">Sandbox OTP: <b className="font-mono">{mockOtpHint}</b></span>}
        </div>
      )}
      {pending && request.provider === "digio" && (
        <div className="flex gap-2 flex-wrap items-center">
          {request.sign_url
            ? <a href={request.sign_url} target="_blank" rel="noreferrer" data-testid="esign-open-digio-link"
                className="inline-flex items-center gap-1 text-accent underline text-xs"><ExternalLink className="w-3.5 h-3.5" /> Open Aadhaar signing page</a>
            : <span className="text-xs text-muted-foreground">Signing link sent to the signer by email / SMS.</span>}
          <Button size="sm" variant="outline" onClick={refresh} disabled={busy} className="rounded-sm gap-1.5" data-testid="esign-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh status
          </Button>
        </div>
      )}
      <div className="flex gap-2">
        {request.status === "completed" && (
          <Button size="sm" onClick={download} className="rounded-sm gap-1.5" data-testid="esign-download-signed-btn">
            <Download className="w-3.5 h-3.5" /> Download signed PDF
          </Button>
        )}
        {pending && (
          <Button size="sm" variant="ghost" onClick={cancel} disabled={busy} className="rounded-sm gap-1.5" data-testid="esign-cancel-btn">
            <XCircle className="w-3.5 h-3.5" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
